/**
 * UpdatePasswordView - Set New Password Page
 * 
 * Allows users to set a new password after clicking the reset link.
 * User is automatically authenticated by Supabase via the email link.
 */

import { useState, useEffect } from 'react';
import { Lock, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface UpdatePasswordViewProps {
  onSuccess: () => void;
}

type SessionState = 'loading' | 'ready' | 'expired' | 'error';

export const UpdatePasswordView: React.FC<UpdatePasswordViewProps> = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Session state management
  const [session, setSession] = useState<Session | null>(null);
  const [sessionState, setSessionState] = useState<SessionState>('loading');

  // ============================================================================
  // Session Recovery Logic
  // ============================================================================

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Recovery-Tokens kommen entweder im Hash (Implicit-Flow:
    // #access_token / type=recovery) ODER als ?code= (PKCE-Flow).
    const hash = window.location.hash;
    const search = new URLSearchParams(window.location.search);
    const hasRecoveryHash = hash.includes('type=recovery') || hash.includes('access_token');
    const pkceCode = search.get('code');
    const hasRecoveryToken = hasRecoveryHash || !!pkceCode;

    const checkExistingSession = async () => {
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        setSession(existingSession);
        setSessionState('ready');
        return true;
      }
      return false;
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (currentSession) {
          setSession(currentSession);
          setSessionState('ready');
          if (timeoutId) clearTimeout(timeoutId);
        }
      } else if (event === 'TOKEN_REFRESHED' && currentSession) {
        setSession(currentSession);
        setSessionState('ready');
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setSessionState('expired');
      }
    });

    const init = async () => {
      const hasSession = await checkExistingSession();
      if (hasSession) return;

      // PKCE: den ?code= aktiv gegen eine Session tauschen.
      if (pkceCode) {
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(pkceCode);
        if (data?.session) {
          setSession(data.session);
          setSessionState('ready');
          return;
        }
        if (exchangeError) {
          setSessionState('expired');
          setError('Der Reset-Link ist ungültig oder abgelaufen. Bitte fordere einen neuen Link an.');
          return;
        }
      }

      if (hasRecoveryToken) {
        // Implicit-Flow: detectSessionInUrl verarbeitet den Hash asynchron → warten.
        timeoutId = setTimeout(() => {
          setSessionState((s) => {
            if (s === 'loading') {
              setError('Der Reset-Link ist ungültig oder abgelaufen. Bitte fordere einen neuen Link an.');
              return 'expired';
            }
            return s;
          });
        }, 5000);
      } else {
        setSessionState('error');
        setError('Ungültiger Zugriff. Bitte nutze den Link aus deiner E-Mail.');
      }
    };

    init();

    return () => {
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!session) {
      setError('Keine aktive Session. Bitte fordere einen neuen Reset-Link an.');
      return;
    }

    if (password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setIsLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        setError(updateError.message);
      } else {
        setIsSuccess(true);
        window.history.replaceState({}, '', window.location.pathname);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading State
  if (sessionState === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
        
        <div className="relative text-center z-10">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="/logo.png" alt="estage.ai" className="w-12 h-12" />
            <h1 className="text-3xl font-bold text-foreground tracking-tight">
              estage<span className="text-primary">.ai</span>
            </h1>
          </div>
          
          <Card className="border-border/50 shadow-2xl backdrop-blur-sm bg-card/95">
             <CardContent className="p-8">
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Lade Benutzerdaten...</p>
             </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error/Expired State
  if (sessionState === 'expired' || sessionState === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
        
        <div className="relative w-full max-w-md z-10">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="/logo.png" alt="estage.ai" className="w-12 h-12" />
            <h1 className="text-3xl font-bold text-foreground tracking-tight">
              estage<span className="text-primary">.ai</span>
            </h1>
          </div>

          <Card className="border-border/50 shadow-2xl backdrop-blur-sm bg-card/95 text-center">
             <CardContent className="p-8">
              <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Link ungültig
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                {error || 'Der Reset-Link ist ungültig oder abgelaufen. Bitte fordere einen neuen Link an.'}
              </p>
              <Button
                asChild
                className="w-full gap-2"
              >
                <a href="/forgot-password">Neuen Link anfordern</a>
              </Button>
             </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Professionelles Virtual Staging für Immobilien
          </p>
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
      
      <div className="relative w-full max-w-md z-10">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo.png" alt="estage.ai" className="w-12 h-12" />
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            estage<span className="text-primary">.ai</span>
          </h1>
        </div>

        {/* Form Card */}
        <Card className="border-border/50 shadow-2xl backdrop-blur-sm bg-card/95">
           <CardContent className="p-8">
            {isSuccess ? (
              // Success State
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  Passwort aktualisiert!
                </h2>
                <p className="text-muted-foreground text-sm mb-4">
                  Dein Passwort wurde erfolgreich geändert. Du wirst automatisch weitergeleitet...
                </p>
                <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
              </div>
            ) : (
              // Form State
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-2">
                    Neues Passwort festlegen
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Gib dein neues Passwort ein.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* New Password Input */}
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Neues Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="new-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10"
                        placeholder="••••••••"
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mindestens 6 Zeichen
                    </p>
                  </div>

                  {/* Confirm Password Input */}
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        placeholder="••••••••"
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isLoading || !session}
                    className="w-full gap-2"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        Passwort speichern
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
           </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Professionelles Virtual Staging für Immobilien
        </p>
      </div>
    </div>
  );
};
